/**
 * System Health Dashboard Page
 *
 * Displays system health metrics for workers, D1, KV, and R2.
 * Capability-gated to users with admin:tenants capability.
 */

import { useQuery } from "@tanstack/react-query";
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  Database,
  HardDrive,
  Clock,
  Server,
  Zap,
  TrendingUp,
} from "lucide-react";
import type { ReactElement } from "react";
import { useState, useCallback, useEffect } from "react";
import { Navigate } from "react-router-dom";

import { apiRequest } from "../../../api/client";
import { TimeSeriesChart } from "../../../components/admin/TimeSeriesChart";
import type { TimeSeriesSeries, ChartColor } from "../../../components/admin/TimeSeriesChart";
import { useHealthHistory } from "../../../hooks/use-health-history";
import type { HealthHistoryPeriod } from "../../../hooks/use-health-history";
import { useAuthStore } from "../../../store/authStore";

import type {
  SystemHealthResponse,
  WorkerHealth,
  D1Metrics,
  KVMetrics,
  R2Metrics,
  WorkerPerformance,
  TimeRange,
  MetricDataPoint,
} from "./types";
import {
  TIME_RANGE_LABELS,
  WORKER_STATUS_INFO,
  AUTO_REFRESH_INTERVAL,
  THRESHOLDS,
  formatBytes,
  formatNumber,
  formatPercent,
  formatMs,
  getThresholdLevel,
} from "./types";

export function HealthDashboardPage(): ReactElement {
  const { hasCapability } = useAuthStore();

  // Check capability
  if (!hasCapability("admin:tenants")) {
    return <Navigate to="/studio" replace />;
  }

  return <HealthDashboardContent />;
}

function HealthDashboardContent(): ReactElement {
  const [timeRange, setTimeRange] = useState<TimeRange>("1h");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [historyPeriod, setHistoryPeriod] = useState<HealthHistoryPeriod>("24h");

  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ["system-health", timeRange],
    queryFn: async () => {
      const response = await apiRequest<SystemHealthResponse>(
        `/api/v1/admin/health?range=${timeRange}`
      );

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    refetchInterval: autoRefresh ? AUTO_REFRESH_INTERVAL : false,
  });

  // Time since last update
  const [lastUpdated, setLastUpdated] = useState<string>("");

  useEffect(() => {
    const updateTime = (): void => {
      if (dataUpdatedAt) {
        const diff = Math.floor((Date.now() - dataUpdatedAt) / 1000);
        if (diff < 60) {
          setLastUpdated(`${diff}s ago`);
        } else {
          setLastUpdated(`${Math.floor(diff / 60)}m ago`);
        }
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [dataUpdatedAt]);

  const handleTimeRangeChange = useCallback((range: TimeRange): void => {
    setTimeRange(range);
  }, []);

  const handleAutoRefreshToggle = useCallback((): void => {
    setAutoRefresh((prev) => !prev);
  }, []);

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Failed to load health data"}
        onRetry={refetch}
      />
    );
  }

  if (!data) {
    return <Navigate to="/studio" replace />;
  }

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">System Health</h1>
          <p className="mt-1 text-foreground-muted">
            Monitor workers, databases, and storage infrastructure
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Last updated */}
          <span className="text-sm text-foreground-muted">
            <Clock className="mr-1 inline-block h-4 w-4" />
            Updated {lastUpdated}
          </span>

          {/* Auto-refresh toggle */}
          <button
            onClick={handleAutoRefreshToggle}
            className={`btn ${autoRefresh ? "btn-primary" : "btn-secondary"}`}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${autoRefresh ? "animate-spin" : ""}`} />
            Auto-refresh {autoRefresh ? "On" : "Off"}
          </button>

          {/* Manual refresh */}
          <button onClick={() => refetch()} disabled={isFetching} className="btn btn-secondary">
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Time range selector */}
      <div className="mb-6 flex items-center gap-2">
        {(Object.keys(TIME_RANGE_LABELS) as TimeRange[]).map((range) => (
          <button
            key={range}
            onClick={() => handleTimeRangeChange(range)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              timeRange === range
                ? "bg-primary text-white"
                : "bg-background-secondary text-foreground-muted hover:text-foreground"
            }`}
          >
            {TIME_RANGE_LABELS[range]}
          </button>
        ))}
      </div>

      {/* Worker health cards */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.workers.map((worker) => (
          <WorkerHealthCard key={worker.name} worker={worker} />
        ))}
      </div>

      {/* Metrics panels */}
      <div className="grid gap-6 lg:grid-cols-2">
        <D1MetricsPanel metrics={data.d1} />
        <KVMetricsPanel metrics={data.kv} />
        <R2MetricsPanel metrics={data.r2} />
        <PerformanceMetricsPanel metrics={data.performance} />
      </div>

      {/* Health history trend charts */}
      <HealthHistorySection period={historyPeriod} onPeriodChange={setHistoryPeriod} />
    </div>
  );
}

// ============================================================================
// Worker Health Card
// ============================================================================

interface WorkerHealthCardProps {
  readonly worker: WorkerHealth;
}

function WorkerHealthCard({ worker }: WorkerHealthCardProps): ReactElement {
  const statusInfo = WORKER_STATUS_INFO[worker.status];
  const latencyLevel = getThresholdLevel(worker.latencyMs, THRESHOLDS.latency);
  const errorLevel = getThresholdLevel(worker.errorRate, THRESHOLDS.errorRate);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Server className="h-5 w-5 text-foreground-muted" />
          <h3 className="font-semibold">{worker.name}</h3>
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
            statusInfo.color === "success"
              ? "bg-success/10 text-success"
              : statusInfo.color === "warning"
                ? "bg-warning/10 text-warning"
                : "bg-error/10 text-error"
          }`}
        >
          <span
            className={`h-2 w-2 rounded-full ${
              statusInfo.color === "success"
                ? "bg-success"
                : statusInfo.color === "warning"
                  ? "bg-warning"
                  : "bg-error"
            }`}
          />
          {statusInfo.label}
        </span>
      </div>

      <dl className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <dt className="text-foreground-muted">Latency</dt>
          <dd className={getMetricClass(latencyLevel)}>{formatMs(worker.latencyMs)}</dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-foreground-muted">Error Rate</dt>
          <dd className={getMetricClass(errorLevel)}>{formatPercent(worker.errorRate)}</dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-foreground-muted">Requests/min</dt>
          <dd>{formatNumber(worker.requestsPerMinute)}</dd>
        </div>
      </dl>
    </div>
  );
}

// ============================================================================
// D1 Metrics Panel
// ============================================================================

interface D1MetricsPanelProps {
  readonly metrics: D1Metrics;
}

function D1MetricsPanel({ metrics }: D1MetricsPanelProps): ReactElement {
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <Database className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">D1 Database</h3>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <MetricBox label="Query Count" value={formatNumber(metrics.queryCount)} />
        <MetricBox label="Avg Query Time" value={formatMs(metrics.avgQueryTimeMs)} />
        <MetricBox label="Rows Read" value={formatNumber(metrics.rowsRead)} />
        <MetricBox label="Rows Written" value={formatNumber(metrics.rowsWritten)} />
        <MetricBox
          label="Database Size"
          value={formatBytes(metrics.databaseSizeMb * 1024 * 1024)}
          className="col-span-2"
        />
      </div>

      <MiniChart data={metrics.timeSeries} label="Query Time (ms)" color="primary" />
    </div>
  );
}

// ============================================================================
// KV Metrics Panel
// ============================================================================

interface KVMetricsPanelProps {
  readonly metrics: KVMetrics;
}

function KVMetricsPanel({ metrics }: KVMetricsPanelProps): ReactElement {
  const hitRateLevel = getThresholdLevel(metrics.cacheHitRate, THRESHOLDS.cacheHitRate, true);

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="h-5 w-5 text-warning" />
        <h3 className="text-lg font-semibold">KV Cache</h3>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <MetricBox label="Read Operations" value={formatNumber(metrics.readOperations)} />
        <MetricBox label="Write Operations" value={formatNumber(metrics.writeOperations)} />
        <MetricBox
          label="Cache Hit Rate"
          value={formatPercent(metrics.cacheHitRate)}
          className={getMetricClass(hitRateLevel)}
        />
        <MetricBox label="Storage Used" value={formatBytes(metrics.storageUsedMb * 1024 * 1024)} />
      </div>

      <MiniChart data={metrics.timeSeries} label="Hit Rate (%)" color="warning" />
    </div>
  );
}

// ============================================================================
// R2 Metrics Panel
// ============================================================================

interface R2MetricsPanelProps {
  readonly metrics: R2Metrics;
}

function R2MetricsPanel({ metrics }: R2MetricsPanelProps): ReactElement {
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <HardDrive className="h-5 w-5 text-success" />
        <h3 className="text-lg font-semibold">R2 Storage</h3>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <MetricBox label="Objects" value={formatNumber(metrics.objectsCount)} />
        <MetricBox label="Storage Used" value={formatBytes(metrics.storageUsedMb * 1024 * 1024)} />
        <MetricBox label="GET Ops" value={formatNumber(metrics.getOperations)} />
        <MetricBox label="PUT Ops" value={formatNumber(metrics.putOperations)} />
        <MetricBox
          label="DELETE Ops"
          value={formatNumber(metrics.deleteOperations)}
          className="col-span-2"
        />
      </div>

      <MiniChart data={metrics.timeSeries} label="Operations" color="success" />
    </div>
  );
}

// ============================================================================
// Performance Metrics Panel
// ============================================================================

interface PerformanceMetricsPanelProps {
  readonly metrics: WorkerPerformance;
}

function PerformanceMetricsPanel({ metrics }: PerformanceMetricsPanelProps): ReactElement {
  const cpuLevel = getThresholdLevel(metrics.cpuTimeMs, THRESHOLDS.cpuTime);
  const errorRate = metrics.requestsTotal > 0 ? metrics.errorsTotal / metrics.requestsTotal : 0;
  const errorLevel = getThresholdLevel(errorRate, THRESHOLDS.errorRate);

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="h-5 w-5 text-purple-500" />
        <h3 className="text-lg font-semibold">Worker Performance</h3>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <MetricBox
          label="CPU Time"
          value={formatMs(metrics.cpuTimeMs)}
          className={getMetricClass(cpuLevel)}
        />
        <MetricBox label="Wall Time" value={formatMs(metrics.wallTimeMs)} />
        <MetricBox label="Total Requests" value={formatNumber(metrics.requestsTotal)} />
        <MetricBox
          label="Total Errors"
          value={formatNumber(metrics.errorsTotal)}
          className={getMetricClass(errorLevel)}
        />
      </div>

      <MiniChart data={metrics.timeSeries} label="Requests" color="purple" />
    </div>
  );
}

// ============================================================================
// Health History Section
// ============================================================================

interface HealthHistorySectionProps {
  readonly period: HealthHistoryPeriod;
  readonly onPeriodChange: (period: HealthHistoryPeriod) => void;
}

const HISTORY_PERIOD_LABELS: Record<HealthHistoryPeriod, string> = {
  "24h": "24h",
  "7d": "7d",
};

const HISTORY_PERIODS: readonly HealthHistoryPeriod[] = ["24h", "7d"];

function HealthHistorySection({ period, onPeriodChange }: HealthHistorySectionProps): ReactElement {
  const { snapshots, isLoading, isError, error } = useHealthHistory(period);

  const latencySeries: readonly TimeSeriesSeries[] = (
    [
      {
        id: "d1",
        name: "D1",
        color: "primary" as ChartColor,
        data: snapshots
          .filter((s) => s.d1LatencyMs !== null)
          .map((s) => ({
            timestamp: s.createdAt,
            value: s.d1LatencyMs as number,
          })),
      },
      {
        id: "kv",
        name: "KV",
        color: "warning" as ChartColor,
        data: snapshots
          .filter((s) => s.kvLatencyMs !== null)
          .map((s) => ({
            timestamp: s.createdAt,
            value: s.kvLatencyMs as number,
          })),
      },
      {
        id: "r2",
        name: "R2",
        color: "success" as ChartColor,
        data: snapshots
          .filter((s) => s.r2LatencyMs !== null)
          .map((s) => ({
            timestamp: s.createdAt,
            value: s.r2LatencyMs as number,
          })),
      },
    ] satisfies TimeSeriesSeries[]
  ).filter((s) => s.data.length > 0);

  return (
    <div className="mt-6 card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Latency Trends</h3>
          <p className="text-sm text-foreground-muted">Infrastructure response times over time</p>
        </div>

        {/* Period selector */}
        <div className="flex items-center gap-2">
          {HISTORY_PERIODS.map((p) => (
            <button
              key={p}
              onClick={() => onPeriodChange(p)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                period === p
                  ? "bg-primary text-white"
                  : "bg-background-secondary text-foreground-muted hover:text-foreground"
              }`}
            >
              {HISTORY_PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      {isError && (
        <p className="text-sm text-error py-4 text-center">{error ?? "Failed to load history"}</p>
      )}

      {!isError && (
        <TimeSeriesChart
          series={latencySeries}
          yAxisLabel="ms"
          height={200}
          showLegend
          showGrid
          showAxisLabels
          formatValue={(v) => `${v.toFixed(0)}ms`}
          emptyMessage="No health snapshots recorded for this period"
          isLoading={isLoading}
        />
      )}
    </div>
  );
}

// ============================================================================
// Shared Components
// ============================================================================

interface MetricBoxProps {
  readonly label: string;
  readonly value: string;
  readonly className?: string;
}

function MetricBox({ label, value, className }: MetricBoxProps): ReactElement {
  return (
    <div className={`rounded-lg bg-background-secondary p-3 ${className ?? ""}`}>
      <dt className="text-xs text-foreground-muted">{label}</dt>
      <dd className="mt-1 text-lg font-semibold">{value}</dd>
    </div>
  );
}

interface MiniChartProps {
  readonly data: readonly MetricDataPoint[];
  readonly label: string;
  readonly color: "primary" | "warning" | "success" | "purple";
}

function MiniChart({ data, label, color }: MiniChartProps): ReactElement {
  if (!data.length) {
    return (
      <div className="h-24 flex items-center justify-center text-foreground-muted text-sm">
        No data available
      </div>
    );
  }

  const values = data.map((d) => d.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;

  const colorClass =
    color === "primary"
      ? "fill-primary/20 stroke-primary"
      : color === "warning"
        ? "fill-warning/20 stroke-warning"
        : color === "success"
          ? "fill-success/20 stroke-success"
          : "fill-purple-500/20 stroke-purple-500";

  // Create SVG path for area chart
  const width = 100;
  const height = 60;
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((d.value - min) / range) * height;
    return `${x},${y}`;
  });

  const linePath = `M${points.join(" L")}`;
  const areaPath = `${linePath} L${width},${height} L0,${height} Z`;

  const firstPoint = data[0];
  const lastPoint = data[data.length - 1];

  return (
    <div className="mt-4">
      <p className="text-xs text-foreground-muted mb-2">{label}</p>
      <svg viewBox={`0 0 ${width} ${height}`} className={`w-full h-16 ${colorClass}`}>
        <path d={areaPath} className="fill-current opacity-20" />
        <path d={linePath} className="stroke-current fill-none stroke-2" />
      </svg>
      {firstPoint && lastPoint && (
        <div className="flex justify-between text-xs text-foreground-muted mt-1">
          <span>{new Date(firstPoint.timestamp).toLocaleTimeString()}</span>
          <span>{new Date(lastPoint.timestamp).toLocaleTimeString()}</span>
        </div>
      )}
    </div>
  );
}

/** Get CSS class based on threshold level */
function getMetricClass(level: "normal" | "warning" | "critical"): string {
  switch (level) {
    case "warning":
      return "text-warning";
    case "critical":
      return "text-error";
    default:
      return "";
  }
}

// ============================================================================
// State Components
// ============================================================================

function LoadingState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="mt-4">Loading system health...</p>
    </div>
  );
}

interface ErrorStateProps {
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="p-6">
      <div className="flex flex-col items-center justify-center py-12">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
          <AlertCircle className="h-6 w-6 text-error" />
        </div>
        <p className="mt-4 text-foreground-muted">{message}</p>
        <button onClick={onRetry} className="btn btn-secondary mt-4">
          <RefreshCw className="mr-2 h-4 w-4" />
          Try again
        </button>
      </div>
    </div>
  );
}
